import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LogOut, LogIn, Home, BedDouble, ClipboardList, ArrowRight,
  RefreshCw, KeyRound, Copy, Check, X, Loader2,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact "today at a glance" widget for the admin home.
 * · Pulls /api/hk/summary and shows partenze / arrivi / in casa
 * · Trigger button for Fullboard planner sync
 * · Popover to view/rotate the governante PIN + share the /pulizie URL
 */
export default function HousekeepingWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const load = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.get(`${API}/hk/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
      setErr(false);
    } catch {
      setErr(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const syncPlanner = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.post(
        `${API}/hk/sync-planner`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: "Planner sincronizzato", description: `${data.inserted || 0} nuove · ${data.updated || 0} aggiornate` });
      await load();
    } catch (e) {
      toast({ title: "Errore sync", description: e?.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  };

  if (err || !data) return null;
  const { counts, partenze, arrivi, in_casa } = data;
  const previews = (list) => list.slice(0, 2).map((r) => r.name).join(" · ") + (list.length > 2 ? ` +${list.length - 2}` : "");

  return (
    <div className="rounded-2xl border border-[#e8dcc8] bg-white shadow-sm overflow-hidden mb-4 relative" data-testid="hk-admin-widget">
      <div className="px-4 py-3 bg-gradient-to-r from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold truncate">
            Oggi in casa · {new Date(data.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="hk-widget-pin-btn"
            onClick={() => setPinOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full bg-[#f7ebdf]/15 hover:bg-[#f7ebdf]/25 border border-[#f7ebdf]/25 px-3 py-1 text-xs font-semibold transition-colors"
            title="PIN Governante e link condivisione"
          >
            <KeyRound className="h-3 w-3" /> PIN
          </button>
          <button
            data-testid="hk-widget-sync-btn"
            onClick={syncPlanner}
            disabled={syncing}
            className="inline-flex items-center gap-1 rounded-full bg-[#f7ebdf]/15 hover:bg-[#f7ebdf]/25 border border-[#f7ebdf]/25 px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-60"
            title="Ricarica prenotazioni da Fullboard"
          >
            <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sync…" : "Sincronizza"}
          </button>
          <Link
            to="/admin/pulizie/oggi"
            data-testid="hk-widget-link"
            className="inline-flex items-center gap-1 rounded-full bg-[#f7ebdf]/15 hover:bg-[#f7ebdf]/25 border border-[#f7ebdf]/25 px-3 py-1 text-xs font-semibold transition-colors"
          >
            Apri planner <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#e8dcc8]">
        <Cell Icon={LogOut}    color="#4a1c17" label="Partenze" count={counts.partenze} preview={previews(partenze)} testId="hk-widget-partenze" />
        <Cell Icon={LogIn}     color="#c5a059" label="Arrivi"   count={counts.arrivi}   preview={previews(arrivi)}   testId="hk-widget-arrivi" />
        <Cell Icon={Home}      color="#7c5a4e" label="In casa"  count={counts.in_casa}  preview={previews(in_casa)}  testId="hk-widget-in-casa" />
        <Cell Icon={BedDouble} color="#9ca3af" label="Libere"   count={counts.libere}   preview={`${counts.total} camere totali`} testId="hk-widget-libere" />
      </div>

      {pinOpen && <PinPopover onClose={() => setPinOpen(false)} />}
    </div>
  );
}

function Cell({ Icon, color, label, count, preview, testId }) {
  return (
    <div className="px-3 py-2.5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-[#7c5a4e]">
        <Icon className="h-3 w-3" style={{ color }} /> {label}
      </div>
      <div className="text-2xl font-bold text-[#2b1410] leading-tight">{count}</div>
      {preview && <div className="text-[11px] text-[#7c5a4e] truncate">{preview}</div>}
    </div>
  );
}

function PinPopover({ onClose }) {
  const [pin, setPin] = useState(null);
  const [edit, setEdit] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const ref = useRef(null);

  const hkUrl = `${window.location.origin}/pulizie`;

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    axios.get(`${API}/hk/admin/pin`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPin(r.data.pin))
      .catch(() => setPin("—"));
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  const save = async () => {
    const next = edit.trim();
    if (!/^\d{4,8}$/.test(next)) {
      toast({ title: "PIN non valido", description: "Deve essere numerico, 4-8 cifre" });
      return;
    }
    if (next === pin) { toast({ title: "Nessuna modifica" }); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/hk/admin/pin`, { pin: next }, { headers: { Authorization: `Bearer ${token}` } });
      setPin(next);
      setEdit("");
      toast({ title: "PIN aggiornato", description: "Le sessioni governante attive dovranno reinserirlo" });
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1400);
  };

  return (
    <div
      ref={ref}
      data-testid="hk-pin-popover"
      className="absolute right-3 top-14 z-30 w-[320px] bg-white rounded-xl shadow-2xl border border-[#e8dcc8] p-4"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[#4a1c17]" />
          <h4 className="font-serif text-sm font-bold text-[#2b1410]">PIN Governante</h4>
        </div>
        <button onClick={onClose} className="text-[#7c5a4e] hover:text-[#4a1c17]" data-testid="hk-pin-popover-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-[#7c5a4e] font-semibold mb-1">PIN attuale</div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 font-mono text-2xl font-bold text-[#4a1c17] tracking-widest bg-[#faf3ee] rounded-lg px-3 py-2" data-testid="hk-pin-current">
          {pin === null ? <Loader2 className="h-5 w-5 animate-spin" /> : pin}
        </div>
        <button
          onClick={() => copy(pin, "pin")}
          disabled={!pin || pin === "—"}
          className="p-2 rounded-lg border border-[#e8dcc8] hover:bg-[#faf3ee] disabled:opacity-40"
          title="Copia PIN"
          data-testid="hk-pin-copy"
        >
          {copied === "pin" ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-[#4a1c17]" />}
        </button>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-[#7c5a4e] font-semibold mb-1">Cambia PIN</div>
      <div className="flex items-center gap-2 mb-3">
        <input
          type="tel"
          inputMode="numeric"
          maxLength={8}
          value={edit}
          onChange={(e) => setEdit(e.target.value.replace(/\D/g, ""))}
          placeholder="Nuovo PIN (4-8 cifre)"
          className="flex-1 font-mono text-center tracking-widest bg-white border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]"
          data-testid="hk-pin-edit-input"
        />
        <button
          onClick={save}
          disabled={busy || edit.length < 4}
          className="rounded-lg bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2 text-sm font-semibold disabled:opacity-50"
          data-testid="hk-pin-save"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
        </button>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-[#7c5a4e] font-semibold mb-1">Link per la governante</div>
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1 text-[11px] font-mono text-[#4a1c17] bg-[#faf3ee] rounded-lg px-3 py-2 truncate" data-testid="hk-pin-url">
          {hkUrl}
        </div>
        <button
          onClick={() => copy(hkUrl, "url")}
          className="p-2 rounded-lg border border-[#e8dcc8] hover:bg-[#faf3ee]"
          title="Copia link"
          data-testid="hk-pin-url-copy"
        >
          {copied === "url" ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-[#4a1c17]" />}
        </button>
      </div>
      <p className="text-[11px] text-[#7c5a4e] italic leading-snug mt-2">
        Consegna alla governante link + PIN. Al cambio PIN, tutte le sessioni attive verranno chiuse.
      </p>
    </div>
  );
}
